import React, { useState, useEffect, useRef } from 'react';
import { navigate } from 'gatsby';
import search from '../../images/search.png';
import useFacebookPixel from '../../utils/useFacebookPixel';
import { search as buscarProductos } from '../../services/front';
import { trackSearchPerformed } from '../../utils/mixpanel';

// Mixpanel (task-1635): "Search Performed" cuando el cliente termina de buscar
// (Enter, PAUSA_MS sin escribir o al salir de la caja), no en cada letra.
const PAUSA_MS = 1000;
let ultimaRegistrada = '';

const normalizar = (texto) => String(texto || '').trim().replace(/\s+/g, ' ').toLowerCase();

async function registrarBusqueda(texto) {
  const termino = String(texto || '').trim();
  const clave = normalizar(termino);
  // Con menos de 2 letras el buscador no busca.
  if (clave.length < 2) return;
  // Enter justo después de la pausa es la misma búsqueda, no otra.
  if (clave === ultimaRegistrada) return;
  ultimaRegistrada = clave;
  try {
    // La misma primera página que muestran los resultados: front.js la comparte
    // (caché y petición en curso), así que no es otra petición al backend.
    const resultado = await buscarProductos(termino);
    trackSearchPerformed(termino, resultado && resultado.total, resultado && resultado.fuzzy);
  } catch (e) {
    // Sin respuesta del buscador no se sabe cuántos resultados hubo: no se registra.
    if (ultimaRegistrada === clave) ultimaRegistrada = '';
  }
}

export const SearchInput = ({ query, onSearch, view, clase }) => {
  const [searchTerm, setSearchTerm] = useState(query);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const inputRef = useRef(null);
  const pausa = useRef(null);
  const { trackSearch } = useFacebookPixel();
  
  // Función helper para obtener usuario
  const getUser = () => {
    try {
      const userData = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      return null;
    }
  };

  // Preservar el texto original del usuario. La normalización la hace el backend.
  const handleSearchChange = (e) => {
    const newQuery = e.target.value;
    setSearchTerm(newQuery);
    onSearch(newQuery);

    clearTimeout(pausa.current);
    pausa.current = null;
    if (normalizar(newQuery).length < 2) {
      // Caja vacía: si después vuelve a buscar lo mismo, es otra búsqueda.
      ultimaRegistrada = '';
      return;
    }
    pausa.current = setTimeout(() => {
      pausa.current = null;
      registrarBusqueda(newQuery);
    }, PAUSA_MS);
  };

  // Salir de la caja (p. ej. para agregar un resultado al carrito) también
  // termina la búsqueda: así queda antes que lo que haga después.
  const handleBlur = () => {
    if (!pausa.current) return;
    clearTimeout(pausa.current);
    pausa.current = null;
    registrarBusqueda(searchTerm);
  };

  useEffect(() => () => clearTimeout(pausa.current), []);

  const handleSearchClick = () => {
    if (inputRef.current) {
      inputRef.current.focus();
      setIsHighlighted(true);
      setTimeout(() => setIsHighlighted(false), 1500);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      clearTimeout(pausa.current);
      pausa.current = null;
      registrarBusqueda(searchTerm);

      // Track Facebook Pixel + CAPI event
      if (searchTerm.trim()) {
        const user = getUser();
        trackSearch(searchTerm.trim(), user);
      }

      // Redirige a la página de búsqueda con el término de búsqueda tal como lo escribió el usuario
      navigate(`/buscar/?q=${encodeURIComponent(searchTerm)}`);
    }
  };

  useEffect(() => {
    setSearchTerm(query);
  }, [query]);

  return (
    <div className={`${view ? "Header__middlebar--search mobileSearch" : "Header__middlebar--search"} ${clase || ''} ${isHighlighted ? 'search-highlight' : ''}`}>
      <button onClick={handleSearchClick}><img src={search} alt="" /></button>
      <input
        ref={inputRef}
        type="text"
        value={searchTerm}
        onChange={handleSearchChange}
        onKeyPress={handleKeyPress}
        onBlur={handleBlur}
        placeholder="¿Qué Buscas?"
        style={{ fontSize: '16px' }}
      />
    </div>
  );
};